/**
 * Spanish dictionary — key tree must exactly match the zh-HK baseline (enforced by i18n:check).
 * Brand names (JKD Coding / Landlink / Bizpulse) and HK$ prices remain untranslated.
 */
import type { Dict } from '../index';

const dict: Dict = {
  // Site meta
  'meta.title': 'JKD Coding | Using No Way as Way — Consultor de Negocios con IA',
  'meta.description': 'JKD Coding fusiona la filosofía Jeet Kune Do de Bruce Lee para ayudar a ONGs, empresas sociales y pymes con diagnóstico de conversión con IA, diseño de sistemas de crecimiento y gestión de relaciones con clientes.',

  // Navbar
  'nav.arsenal': 'Arsenal',
  'nav.components': 'Componentes',
  'nav.custom': 'A medida',
  'nav.philosophy': 'Filosofía',
  'nav.consult': 'Consulta gratuita',

  // Hero
  'hero.kicker': 'Be Water, My Friend',
  'hero.titleA': 'Sabiduría del agua',
  'hero.titleB': 'Resolver problemas de negocio con rapidez y precisión',
  'hero.subtitle': 'JKD Coding diseña sistemas de conversión con IA para ONGs, empresas sociales y pymes — sin imponer tecnología, solo encontrando la forma más natural para tu negocio: crecimiento de donantes, retención de clientes y alerta temprana de flujo de caja en un solo lugar.',
  'hero.cta': 'Reservar diagnóstico de IA',
  'hero.approach': 'Conoce nuestro método',
  'hero.quote': 'Vacía tu mente, sin forma ni límites, como el agua.',

  // Services
  'services.tag': 'Nuestra práctica',
  'services.title': 'No vendemos herramientas. Resolvemos problemas.',
  'services.subtitle': 'Cada proyecto parte del cuello de botella de tu negocio, y luego decidimos qué forma debe tomar la IA.',
  'services.m1.eyebrow': '01 / Crecimiento',
  'services.m1.title': 'Crecimiento de donantes y clientes',
  'services.m1.problem': 'Los visitantes llegan pero no convierten, y el costo de adquisición no deja de subir.',
  'services.m1.solution': 'Diseñamos embudos de conversión impulsados por IA que convierten cada interacción en una relación duradera.',
  'services.m1.result': 'Mayor conversión, menor costo de adquisición',
  'services.m2.eyebrow': '02 / Relaciones',
  'services.m2.title': 'Operación de relaciones con clientes',
  'services.m2.problem': 'Los seguimientos se escapan y los clientes se enfrían poco a poco.',
  'services.m2.solution': 'Convertimos la comunicación uno a uno en una operación de relaciones predecible y escalable.',
  'services.m2.result': 'Menos fuga, mayor retención',
  'services.m3.eyebrow': '03 / Riesgo',
  'services.m3.title': 'Flujo de caja y alerta temprana de riesgos',
  'services.m3.problem': 'La morosidad y la fuga hacia la competencia solo se descubren cuando ya ocurrió.',
  'services.m3.solution': 'La IA supervisa señales de comportamiento a diario y te avisa antes de que el riesgo se materialice.',
  'services.m3.result': 'Protege el flujo de caja, actúa con antelación',
  'services.m4.eyebrow': '04 / Adopción',
  'services.m4.title': 'Adopción del cambio y eficiencia operativa',
  'services.m4.problem': 'El sistema está en marcha, pero el personal se resiste a usarlo y los costos de formación se acumulan.',
  'services.m4.solution': 'Diseñamos rutas de adopción que parten de los hábitos existentes para que el cambio se sienta natural.',
  'services.m4.result': 'Mayor adopción, menos errores',
  'services.m5.eyebrow': '05 / CONOCIMIENTO',
  'services.m5.problem': 'La reunión termina y la experiencia se evapora con ella.',
  'services.problemLabel': 'El problema',
  'services.solutionLabel': 'Nuestro enfoque',
  'services.cta': 'Reserva un diagnóstico sin presión',

  // Arsenal
  'arsenal.tag': 'READY-TO-DEPLOY',
  'arsenal.title': 'Arsenal',
  'arsenal.subtitle': 'Soluciones por escenario, despliegue rápido, listas para la batalla',
  'arsenal.blindbox.title': 'Sistema Blind Box',
  'arsenal.blindbox.subtitle': 'Herramienta de marketing estacional',
  'arsenal.blindbox.desc': 'Motor de probabilidades, gestión de premios, mecanismos virales sociales y panel de analítica. Ideal para recaudación de ONGs, promociones de e-commerce e interacción con influencers.',
  'arsenal.blindbox.f1': 'Temas estacionales (Año Nuevo/Navidad/Otoño)',
  'arsenal.blindbox.f2': 'Probabilidades y mecanismo de garantía personalizables',
  'arsenal.blindbox.f3': 'Desbloqueo por compartir en redes',
  'arsenal.blindbox.f4': 'Panel de analítica completo',
  'arsenal.blindbox.f5': 'Soporte por video de 30 minutos',
  'arsenal.whatsapp.title': 'Sistema de seguimiento automático personalizado por WhatsApp',
  'arsenal.whatsapp.subtitle': 'Motor de relaciones con clientes',
  'arsenal.whatsapp.desc': 'Flujos de trabajo preconfigurados y flexibles: recuperación de carritos abandonados, actualizaciones de pedidos, confirmación de citas, encuestas de satisfacción.',
  'arsenal.whatsapp.f1': 'Biblioteca de plantillas de mensajes',
  'arsenal.whatsapp.f2': 'Configuración de chatbot',
  'arsenal.whatsapp.f3': 'Integración con sistemas CRM',
  'arsenal.whatsapp.f4': 'Despliegue en 30 minutos',
  'arsenal.whatsapp.f5': 'Soporte técnico continuo',
  'arsenal.promise': '10 minutos para tener tu embudo de conversión',
  'arsenal.priceFrom': 'Desde',

  // Products
  'products.tag': 'NEW RELEASES',
  'products.title': 'Herramientas de negocio con IA',
  'products.subtitle': 'Asistentes de IA diseñados para pymes. Prueba la demo interactiva ahora.',
  'products.cta': 'Probar ahora',
  'products.landlink.title': 'Landlink',
  'products.landlink.subtitle': 'Asistente de adopción de hábitos con IA',
  'products.landlink.desc': 'Resuelve el problema de "el sistema está en marcha, pero el personal o los clientes no lo usan". La IA lee los hábitos existentes y prepara por adelantado los pasos más comunes, convirtiendo flujos de varios pasos en uno solo.',
  'products.landlink.f1': 'Lee los hábitos existentes para preparar el siguiente paso',
  'products.landlink.f2': 'Resumen de adopción para dirección + recordatorio diario por WhatsApp',
  'products.landlink.f3': 'Puntos individuales para el personal y repetición con un clic',
  'products.landlink.f4': 'Convierte 5 pasos en 1, reduciendo costos de formación y errores',
  'products.bizpulse.title': 'Bizpulse',
  'products.bizpulse.subtitle': 'Monitor de riesgo de clientes con IA',
  'products.bizpulse.desc': 'Supervisa el comportamiento de los clientes 24/7 y detecta a tiempo pagos retrasados, fuga y cambios hacia la competencia. Actúa antes de que ocurra la morosidad y protege tu flujo de caja.',
  'products.bizpulse.f1': 'Revisión diaria de registros de clientes con IA',
  'products.bizpulse.f2': 'Detección automática de pagos más lentos y enfriamiento del contacto',
  'products.bizpulse.f3': 'Notificaciones de alerta de riesgo instantáneas',
  'products.bizpulse.f4': 'Sin necesidad de saber de IA, listo para usar',

  // Components
  'components.tag': 'COMPONENTS LIBRARY',
  'components.title': 'Componentes',
  'components.subtitle': 'Capacidades modulares, combínalas a demanda, autonomía técnica',
  'components.quote': '"Pensar es el problema, hacer es la respuesta"',
  'components.scroll.name': 'Módulo de animaciones al desplazar',
  'components.scroll.tech': 'Intersection Observer API',
  'components.scroll.desc': 'Soporta 12 efectos activados por desplazamiento — fundido, deslizamiento, zoom, rotación — con umbrales y retardos configurables',
  'components.i18n.name': 'Framework i18n bilingüe',
  'components.i18n.tech': 'Parámetros URL / detección del navegador',
  'components.i18n.desc': 'Sistema de cambio de contenido basado en parámetros URL o detección automática del idioma del navegador, con soporte RTL reservado',
  'components.cf-track.name': 'Seguimiento con Cloudflare Workers',
  'components.cf-track.tech': 'Serverless',
  'components.cf-track.desc': 'Seguimiento de comportamiento de usuarios sin servidores, con eventos personalizados, segmentación de audiencias y GA4 en paralelo',
  'components.cf-webhook.name': 'Cloudflare Workers Webhook',
  'components.cf-webhook.tech': 'HTTP Endpoints',
  'components.cf-webhook.desc': 'Endpoints HTTP estandarizados con validación de solicitudes, límites de tasa e integración con Zapier/Make',
  'components.og.name': 'Kit de optimización de etiquetas OG',
  'components.og.tech': 'Dynamic Meta Tags',
  'components.og.desc': 'Generación dinámica de etiquetas Open Graph con pruebas A/B y simulación de vista previa en redes sociales',
  'components.full.title': 'Biblioteca completa de componentes',
  'components.full.desc': 'Todos los componentes + soporte prioritario + actualizaciones trimestrales. Ideal para proyectos medianos y grandes que requieren capacidades técnicas sistemáticas.',
  'components.full.tag1': 'Todos los componentes',
  'components.full.tag2': 'Soporte prioritario',
  'components.full.tag3': 'Actualizaciones trimestrales',
  'components.perYear': '/año',
  'components.orMonthly': 'o HK$200/mes',

  // Custom
  'custom.tag': 'CUSTOM DOJO',
  'custom.title': 'A medida',
  'custom.subtitle': 'Embudos de conversión totalmente a medida, de cero a uno, creando tu máquina de conversión única',
  'custom.price': 'Cotización del proyecto',
  'custom.priceNote': 'Personalizado según alcance y complejidad',
  'custom.phase1.week': 'Semana 1',
  'custom.phase1.title': 'Investigación de competidores y análisis de oportunidades',
  'custom.phase1.desc': 'Análisis sistemático de 5-10 competidores principales: ingeniería inversa de mapas de calor, puntuación de rendimiento, solapamiento de palabras clave SEO',
  'custom.phase1.deliverable': 'Informe de análisis de brechas de conversión',
  'custom.phase2.week': 'Semana 1–2',
  'custom.phase2.title': 'Estrategia de textos y arquitectura de información',
  'custom.phase2.desc': 'Entrevistas de usuario Jobs-to-be-Done, 10 variantes de titular, estrategia de prueba social, optimización de verbos CTA',
  'custom.phase2.deliverable': 'Manual de estrategia de textos',
  'custom.phase3.week': 'Semana 2–4',
  'custom.phase3.title': 'Diseño UI/UX y validación de prototipo',
  'custom.phase3.desc': 'Modo sprint de doble vía, prototipo interactivo de alta fidelidad en Figma, pruebas en dispositivos reales',
  'custom.phase3.deliverable': 'Prototipo clicable + especificaciones de diseño',
  'custom.phase4.week': 'Semana 4–6',
  'custom.phase4.title': 'Desarrollo técnico y despliegue',
  'custom.phase4.desc': 'Construcción de la capa de interfaz de usuario, configuración de lógica JSON, despliegue en el borde con Cloudflare Workers',
  'custom.phase4.deliverable': 'Sitio web de producción',
  'custom.phase5.week': 'Semana 6–8',
  'custom.phase5.title': 'Seguimiento de datos y optimización',
  'custom.phase5.desc': 'Análisis de tráfico GA4, mapas de calor de comportamiento Hotjar, framework de pruebas A/B, panel de conversión personalizado',
  'custom.phase5.deliverable': 'Informe de monitoreo de datos',
  'custom.cta': 'Reservar consulta gratuita',
  'custom.ctaNote': 'Sin compromiso, primero entendemos las necesidades de tu negocio',

  // Philosophy
  'philosophy.tag': 'PHILOSOPHY',
  'philosophy.title': 'Filosofía',
  'philosophy.subtitle': 'Metodología vibe-coding, enseñarte a pescar en vez de darte pescado',
  'philosophy.pillar1.title': 'Filosofía Jeet Kune Do',
  'philosophy.pillar1.desc': 'La filosofía de Bruce Lee «usar ningún camino como camino» se encuentra con el desarrollo ágil moderno, explorando cómo la sabiduría de las artes marciales se transforma en mentalidad de desarrollo digital',
  'philosophy.pillar2.title': 'Framework metodológico RPE',
  'philosophy.pillar2.desc': 'Manual completo de Rapid Prototyping & Execution: entrevistas de requisitos, selección de herramientas de prototipado, pruebas con usuarios, ritmo de iteración',
  'philosophy.pillar3.title': 'Reseñas de herramientas y práctica',
  'philosophy.pillar3.desc': 'Reseñas en profundidad de Bubble, Webflow, Framer y otras plataformas no-code/low-code. Sin adornos, construyendo autoridad objetiva',

  // FAQ
  'faq.tag': 'FAQ',
  'faq.title': 'Preguntas frecuentes',
  'faq.subtitle': 'Respuestas directas sobre cómo funciona JKD Coding',
  'faq.q1': '¿A qué tipo de organizaciones sirven?',
  'faq.a1': 'Servimos a ONGs, empresas sociales y pymes de todo el mundo. Ya sea crecimiento de donantes, gestión de relaciones con clientes o alerta temprana de flujo de caja, siempre partimos de tu problema real de negocio.',
  'faq.q2': '¿Qué es el diagnóstico de conversión con IA y qué ocurre en él?',
  'faq.a2': 'Es una conversación consultiva uno a uno de 30 minutos. Aclaramos tu mayor cuello de botella de negocio y encontramos el punto de entrada más natural para la IA; después recibes recomendaciones de mejora concretas, sin ninguna presión de venta.',
  'faq.q3': '¿Cómo se calculan los precios?',
  'faq.a3': 'Las soluciones listas empiezan en HK$800 (Blind Box System) y HK$1,500 (seguimiento automático por WhatsApp); los componentes individuales cuestan HK$300–800 y la biblioteca completa HK$2,000/año; los proyectos a medida se cotizan por alcance, normalmente HK$15,000–50,000.',
  'faq.q4': '¿Qué tan rápido podemos lanzarnos?',
  'faq.a4': 'Las soluciones listas se despliegan en tan solo 30 minutos; la biblioteca de componentes se entrega en días; los proyectos a medida avanzan por cinco fases y suelen lanzarse en 6–8 semanas.',
  'faq.q5': 'No somos técnicos, ¿podemos trabajar con ustedes?',
  'faq.a5': 'Por supuesto. Nosotros nos encargamos de los detalles técnicos; tú solo necesitas describir tus objetivos de negocio. Todas las consultas se responden en 24 horas, y tras confirmar el plan hay soporte técnico continuo.',
  'faq.author': 'Autor: Equipo de JKD Coding',
  'faq.updated': 'Actualizado en septiembre de 2026',

  // Contact
  'contact.tag': 'GET IN TOUCH',
  'contact.title': 'Comienza tu viaje',
  'contact.subtitle': '"La vida... es como cagar, a veces blanda, a veces dura; a veces fluida, a veces no!"',
  'contact.form.title': 'Enviar mensaje',
  'contact.form.name': 'Nombre',
  'contact.form.namePlaceholder': 'Tu nombre',
  'contact.form.email': 'Correo electrónico',
  'contact.form.emailPlaceholder': 'your@email.com',
  'contact.form.business': 'Tipo de negocio',
  'contact.form.businessPlaceholder': 'Selecciona tu tipo de negocio',
  'contact.form.businessNgo': 'ONG / Empresa social',
  'contact.form.businessSme': 'Pyme',
  'contact.form.businessPersonal': 'Marca personal',
  'contact.form.businessOther': 'Otro',
  'contact.form.message': 'Mensaje',
  'contact.form.messagePlaceholder': 'Describe brevemente tus necesidades o preguntas...',
  'contact.form.submit': 'Enviar mensaje',
  'contact.form.processing': 'Procesando...',
  'contact.form.waNote': 'El siguiente paso verifica tu identidad por WhatsApp — asegúrate de poder usar WhatsApp.',
  'contact.verify.title': 'Verifica tu WhatsApp',
  'contact.verify.desc': 'Haz clic en el botón de abajo para abrir WhatsApp y enviar el mensaje de verificación y completar tu envío.',
  'contact.verify.codeLabel': 'Tu código de verificación / Your Verification Code',
  'contact.verify.openWa': 'Abrir WhatsApp para verificar / Open WhatsApp',
  'contact.verify.prefillNote': 'El mensaje de verificación se rellenará automáticamente / Verification message will be pre-filled',
  'contact.verify.back': '← Volver / Go Back',
  'contact.success.title': '¡Verificación exitosa!',
  'contact.success.desc': 'Hemos recibido tu consulta y responderemos dentro de 24 horas.',
  'contact.quick.title': 'Contacto rápido',
  'contact.quick.whatsapp': 'Consulta por WhatsApp',
  'contact.quick.whatsappDesc': 'Respuesta inmediata, reserva consulta gratuita',
  'contact.quick.response': 'Compromiso de respuesta rápida',
  'contact.quick.responseDesc': 'Todas las consultas respondidas en 24 horas',
  'contact.promise': '"Ya seas una ONG, empresa social o pyme, encontraremos la forma más directa de ayudarte a descubrir la mejor solución. No te preocupes por los detalles técnicos, solo cuéntanos tus objetivos de negocio."',
  'contact.promiseAuthor': '— Equipo de JKD Coding',
  'contact.alertFail': 'Error al enviar. Inténtalo de nuevo o contáctanos directamente por WhatsApp.',
  'contact.waPrefill': '¡Hola! Me gustaría conocer más sobre los servicios de JKD Coding.',

  // Footer
  'footer.desc': 'Fusionando la filosofía Jeet Kune Do de Bruce Lee con «usar ningún camino como camino» como pensamiento innovador central, construyendo embudos de conversión para ONGs, empresas sociales y pymes.',
  'footer.products': 'Productos',
  'footer.content': 'Contenido',
  'footer.about': 'Acerca de',
  'footer.aboutUs': 'Sobre nosotros',
  'footer.contact': 'Contacto',
  'footer.faq': 'Preguntas frecuentes',
  'footer.privacy': 'Política de privacidad',
  'footer.terms': 'Términos del servicio',
  'footer.rights': 'All rights reserved.',
  // Diagnostic share card (P0 referral entry)
  'share.title': 'Recomienda a un amigo',
  'share.desc': '¿Conoces una empresa que lucha con la adopción de sistemas? Comparte con ella el diagnóstico gratuito.',
  'share.copy': 'Copiar enlace',
  'share.copied': '¡Copiado!',
  'share.whatsapp': 'Compartir por WhatsApp',
  'share.more': 'Más formas de compartir',
  'share.prefill': 'Un amigo me recomendó probar el diagnóstico de conversión con IA gratuito de JKD Coding (30 minutos, sin presión de venta):',

  // Diagnostic page (/diagnosis)
  'diag.tag': 'Llamada estratégica de 30 minutos',
  'diag.title': 'Diagnóstico de conversión con IA',
  'diag.subtitle': '30 minutos para aclarar el cuello de botella de tu negocio y encontrar el punto de entrada más natural para la IA.',
  'diag.back': 'Volver al inicio',
  'diag.step1Title': 'Cuéntanos tu situación',
  'diag.step1Desc': 'Tras enviarlo, nuestro consultor te contactará dentro de 1 día hábil.',
  'diag.name': 'Nombre *',
  'diag.organization': 'Organización *',
  'diag.role': 'Cargo *',
  'diag.email': 'Correo electrónico *',
  'diag.businessType': 'Tipo de negocio *',
  'diag.businessPlaceholder': 'Selecciona',
  'diag.ngo': 'ONG / Empresa social',
  'diag.sme': 'Pyme',
  'diag.personal': 'Marca personal',
  'diag.other': 'Otro',
  'diag.challenge': '¿Cuál es tu mayor desafío de conversión? *',
  'diag.challengePlaceholder': 'Selecciona',
  'diag.challenge1': 'Llegan visitantes pero no convierten',
  'diag.challenge2': 'Los seguimientos se pierden o la respuesta es muy lenta',
  'diag.challenge3': 'Morosidad o riesgo de fuga difícil de predecir',
  'diag.challenge4': 'El equipo se resiste a usar el nuevo sistema',
  'diag.challenge5': 'Contenido insuficiente para seguir comunicando',
  'diag.goals': 'Objetivo de negocio que quieres resolver primero *',
  'diag.goalsPlaceholder': 'Describe brevemente el resultado que buscas, p. ej. aumentar la tasa de finalización de donaciones un 30%',
  'diag.budget': 'Rango de presupuesto (opcional)',
  'diag.budgetPlaceholder': 'Selecciona',
  'diag.budget1': 'HK$8,000 – 20,000',
  'diag.budget2': 'HK$20,000 – 50,000',
  'diag.budget3': 'HK$50,000 – 150,000',
  'diag.budget4': 'HK$150,000+',
  'diag.privacy': 'Tu información solo se usa para organizar esta conversación y nunca se revende.',
  'diag.submit': 'Reservar mi diagnóstico',
  'diag.submitting': 'Procesando...',
  'diag.verifyTitle': 'Verifica tu WhatsApp',
  'diag.verifyDesc': 'Haz clic en el botón de abajo para abrir WhatsApp y enviar el mensaje de verificación para completar tu reserva.',
  'diag.code': 'Tu código de verificación',
  'diag.openWhatsApp': 'Abrir WhatsApp para verificar',
  'diag.prefilled': 'El mensaje de verificación se rellenará automáticamente',
  'diag.goBack': 'Volver',
  'diag.successTitle': '¡Verificación exitosa!',
  'diag.successDesc': 'Hemos recibido tu solicitud de diagnóstico y responderemos dentro de 24 horas.',
  'diag.alertFail': 'Error al enviar. Inténtalo de nuevo.',
  'diag.msgOrg': 'Organización',
  'diag.msgRole': 'Cargo',
  'diag.msgChallenge': 'Mayor desafío',
  'diag.msgGoals': 'Objetivo a resolver primero',
  'diag.msgBudget': 'Rango de presupuesto',
  'diag.msgUnfilled': 'No indicado',

  // Landlink (/landlink)
  'landlink.brandName': 'Landlink',
  'landlink.backAria': 'Volver al inicio',
  'landlink.brandAria': 'Landlink',
  'landlink.zero.kicker': 'Asistente de adopción de hábitos con IA',
  'landlink.zero.titleA': 'El sistema está en marcha,',
  'landlink.zero.titleB': '¿pero el personal o los clientes no lo usan?',
  'landlink.zero.body': 'Landlink lee las rutinas existentes y prepara las tareas más comunes,',
  'landlink.zero.bodyStrong': 'convirtiendo cinco pasos en uno.',
  'landlink.zero.cta': 'Ver dónde está la fricción',
  'landlink.zero.dashLabel': 'ADOPTION RATE',
  'landlink.one.kicker': 'La IA está desglosando tu fricción de adopción',
  'landlink.one.title': 'La tecnología se lanza; los hábitos no siempre.',
  'landlink.one.body': 'Pulsa las 3 señales para ver por qué el personal o los clientes evitan el sistema.',
  'landlink.one.closeAria': 'Cerrar',
  'landlink.one.continueNext': 'Continuar con la siguiente señal →',
  'landlink.one.continueDone': 'Ver cómo toma el control la IA →',
  'landlink.one.progressPre': 'Señales vistas:',
  'landlink.one.progressPost': '',
  'landlink.one.f1.eyebrow': 'Fricción: familiaridad',
  'landlink.one.f1.title': 'La forma antigua cero costo mental',
  'landlink.one.f1.text': 'El papel y el bolígrafo ya son instinto; incluso un paso extra en un sistema nuevo se abandona en el momento más ocupado. El problema no son las funciones, sino el costo de cambiar de hábito.',
  'landlink.one.f2.eyebrow': 'Fricción: miedo a equivocarse',
  'landlink.one.f2.title': 'El personal de primera línea teme pulsar mal',
  'landlink.one.f2.text': 'Cuando el resultado de una operación no es claro, el personal toma el camino antiguo más seguro. Lo que necesitan no es otra formación, sino retroalimentación instantánea y certera.',
  'landlink.one.f3.eyebrow': 'Fricción: sin recompensa',
  'landlink.one.f3.title': 'El cambio no va conmigo',
  'landlink.one.f3.text': 'La empresa gana eficiencia; el personal de primera línea solo siente la molestia. El beneficio debe llegar al individuo para que la adopción pase de orden a elección.',
  'landlink.two.kicker': 'Los agentes de IA no son un concepto — mira uno trabajar de verdad',
  'landlink.two.titlePre': '5 pasos en ',
  'landlink.two.titleHighlight': '1 paso',
  'landlink.two.titlePost': '.',
  'landlink.two.body': 'El mismo pedido, dos formas. La brecha es la elección que el personal y los clientes enfrentan cada día.',
  'landlink.two.startHint': 'Pulsa iniciar para ver cómo la IA comprime cinco pasos en una sola acción usando los hábitos existentes.',
  'landlink.two.startCta': 'Iniciar la comparación',
  'landlink.two.classicLabel': 'La forma antigua',
  'landlink.two.classicStepsLabel': '5 pasos',
  'landlink.two.step1': 'Abrir el sistema de pedidos',
  'landlink.two.step2': 'Seleccionar cada artículo',
  'landlink.two.step3': 'Introducir cantidad y especificaciones',
  'landlink.two.step4': 'Revisar datos de entrega',
  'landlink.two.step5': 'Confirmar y enviar',
  'landlink.two.orderLabel': 'Pedido de ejemplo',
  'landlink.two.orderItem': '🥬 Verduras × 2',
  'landlink.two.aiLabel': 'La forma IA',
  'landlink.two.aiStepsLabel': '1 acción',
  'landlink.two.aiReadyLabel': 'La IA lo tiene listo',
  'landlink.two.aiOrder': '🥬 Verduras × 2 · a la sucursal Central · esta mañana',
  'landlink.two.evidenceIntro': 'Según hábitos anteriores',
  'landlink.two.ev1.label': 'Últimos 4 pedidos',
  'landlink.two.ev1.value': 'Todos verduras × 2',
  'landlink.two.ev2.label': 'Lugar de entrega habitual',
  'landlink.two.ev2.value': 'Sucursal Central · 9 veces',
  'landlink.two.ev3.label': 'Hábito del lunes',
  'landlink.two.ev3.value': 'Normalmente repone a las 10 a.m.',
  'landlink.two.confirm': 'Lo mismo de siempre · Confirmar y enviar',
  'landlink.two.confirmed': 'Confirmado',
  'landlink.two.aiNote': 'La IA solo usa registros de la empresa autorizados — cada sugerencia tiene su fuente.',
  'landlink.three.titlePre': 'No pedimos a la gente que cambie —',
  'landlink.three.titleMid': '',
  'landlink.three.titleHighlight': 'hacemos que valga la pena',
  'landlink.three.titlePost': '.',
  'landlink.three.body': 'Motivación, capacidad e indicaciones: todo hace falta. Landlink reduce la fricción y aumenta la recompensa para dirección y primera línea a la vez.',
  'landlink.three.cta': 'Iniciar la conversación',
  'landlink.three.card1.title': 'Capa directiva | Impulsada de arriba abajo',
  'landlink.three.card1.copy': 'Da a los gestores visibilidad del progreso de adopción y una base para recordatorios proactivos diarios.',
  'landlink.three.card2.title': 'Capa de primera línea | Incentivos que llegan al individuo',
  'landlink.three.card2.copy': 'Hace que el nuevo sistema sea más rápido, más fácil y digno de abrir cada día.',
  'landlink.four.kicker': 'Logra que el sistema realmente arraigue',
  'landlink.four.titlePre': 'El próximo en cambiar',
  'landlink.four.titleMid': 'podría ser',
  'landlink.four.titleHighlight': 'tu sistema',
  'landlink.four.titlePost': '.',
  'landlink.four.body': 'Cuéntanos qué sistema estás implantando y encontraremos el paso que merece ir primero.',
  'landlink.four.ctaLoading': 'Conectándote con un consultor…',
  'landlink.four.cta': 'Saber más por WhatsApp',
  'landlink.four.note': 'No se necesitan conocimientos técnicos · te guiamos paso a paso',
  'landlink.four.waPrefill': 'Hola, me gustaría conocer Landlink, el asistente de adopción de hábitos con IA.',

  // Bizpulse (/bizpulse)
  'bizpulse.brandName': 'Bizpulse',
  'bizpulse.backAria': 'Volver al inicio',
  'bizpulse.title': 'Contrata un oficial de riesgos de IA que nunca se descuida',
  'bizpulse.kicker': 'Monitoreo de riesgo de clientes con IA',
  'bizpulse.heroBody': 'Vigilando cada cliente por ti, 24/7.',
  'bizpulse.heroStrong': 'Pagos retrasados, fuga o paso a la competencia — la IA te avisa primero.',
  'bizpulse.heroCta': 'Ver cómo la IA vigila mis clientes',
  'bizpulse.scanLabel': 'La IA está escaneando tus clientes',
  'bizpulse.one.title': 'La IA ve el riesgo antes de que ocurra.',
  'bizpulse.one.body': 'Pulsa las 3 alertas rojas para ver qué puede detectar la IA con antelación.',
  'bizpulse.one.closeAria': 'Cerrar',
  'bizpulse.one.continueNext': 'Continuar con la siguiente alerta →',
  'bizpulse.one.continueDone': 'Ver cómo trabaja el oficial de riesgos IA →',
  'bizpulse.one.progressPre': 'Alertas vistas:',
  'bizpulse.one.progressPost': '',
  'bizpulse.one.s1.title': 'Posible morosidad',
  'bizpulse.one.s1.tag': 'La IA notó: los pagos se están ralentizando',
  'bizpulse.one.s1.detail': 'Las fechas de pago de este cliente se retrasan una y otra vez. La IA te avisa antes de que se convierta en deuda incobrable para que reclames a tiempo y protejas tu flujo de caja.',
  'bizpulse.one.s2.title': 'Posible fuga',
  'bizpulse.one.s2.tag': 'La IA notó: el cliente se está enfriando',
  'bizpulse.one.s2.detail': 'Menos consultas, respuestas más lentas, menos interacción. La IA monitorea a diario para que puedas retenerlo antes de que se vaya.',
  'bizpulse.one.s3.title': 'Posible paso a la competencia',
  'bizpulse.one.s3.tag': 'La IA notó: cambiaron los hábitos de pedido',
  'bizpulse.one.s3.detail': 'La frecuencia y el volumen de pedidos están fuera de lo normal. La IA detecta estos cambios sutiles para que aproveches la última ventana de seguimiento.',
  'bizpulse.two.step1.title': 'Conectar registros',
  'bizpulse.two.step1.copy': 'Un solo lugar para los datos de clientes',
  'bizpulse.two.step2.title': 'Escaneos diarios de IA',
  'bizpulse.two.step2.copy': 'Detectar comportamiento anómalo',
  'bizpulse.two.step3.title': 'Alertas instantáneas',
  'bizpulse.two.step3.copy': 'Te comunica el riesgo directamente',
  'bizpulse.two.step4.title': 'Seguimiento temprano',
  'bizpulse.two.step4.copy': 'Protege el negocio y la caja',
  'bizpulse.two.finalSmall': 'Tu oficial de riesgos IA',
  'bizpulse.two.titlePre': 'No necesitas saber de IA.',
  'bizpulse.two.titleHighlight': 'La IA vigila a tus clientes automáticamente.',
  'bizpulse.two.finalBody': 'Escanea a diario, nunca se descuida; en cuanto hay riesgo, te avisan al instante.',
  'bizpulse.two.ctaLoading': 'Conectándote con un consultor…',
  'bizpulse.two.cta': 'Saber más por WhatsApp',
  'bizpulse.two.note': 'No se necesitan conocimientos técnicos · te guiamos paso a paso',
  'bizpulse.two.waPrefill': 'Hola, me gustaría saber cómo contratar un oficial de riesgos de IA que monitoree el riesgo de mis clientes.',

  // Promo (/promo)
  'promo.kicker': 'Inmobiliaria × automatización de redes sociales',
  'promo.titlePre': 'Actualizaciones de propiedades, publicaciones, analítica —',
  'promo.titleHighlight': 'un sistema lo resuelve todo.',
  'promo.heroLead': 'En el momento en que entra un nuevo inmueble, se convierte en una publicación profesional de FB en 10 segundos — marca de agua automática antirrobo, enlaces cortos con seguimiento y estadísticas de qué propiedad atrae más interés.',
  'promo.heroBold': '"De fotos a video"',
  'promo.heroTail': ' — mínimo esfuerzo, máxima experiencia para el cliente.',
  'promo.videoCaption': '▲ Demo en vivo: datos del inmueble → video profesional de un clic (música, subtítulos, tarjeta de contacto y QR automáticos)',
  'promo.ctaDemo': 'Reservar demo por WhatsApp',
  'promo.ctaTry': 'Pruébalo en línea',
  'promo.waPrefill': 'Me gustaría conocer la solución de sincronización inmuebles-redes sociales',
  'promo.painTitle': 'Tres cosas que cada agente enfrenta a diario',
  'promo.pain1.t': 'Las propiedades cambian a diario',
  'promo.pain1.d': 'Nuevas, alquiladas, vendidas — tras actualizar la web, también hay que publicar en redes a mano, corriendo de un lado a otro',
  'promo.pain2.t': 'Las fotos se roban apenas se suben',
  'promo.pain2.d': 'Las fotos de propiedades tomadas con tanto esfuerzo se descargan y reutilizan en un instante — ni la edición con IA puede evitarlo',
  'promo.pain3.t': 'El dinero de la publicidad se va en agua',
  'promo.pain3.d': 'Publicas sin saber qué propiedad recibe visitas ni cuántos clics — sin datos no hay dirección',
  'promo.stepsTitlePre': 'De la propiedad a la publicación —',
  'promo.stepsTitleHighlight': '3 pasos, 10 segundos',
  'promo.stepsNote': 'Capturas reales del sistema a continuación, no son conceptos',
  'promo.screenshotAlt': 'Vista real del sistema: área de promoción social',
  'promo.screenshotCaption': '▲ Backend en vivo: selecciona fotos → texto generado automáticamente (editable) → publicación de un clic directo a FB',
  'promo.step1.t': 'Elige fotos',
  'promo.step1.d': 'Selecciona fotos del inmueble; el sistema añade una marca de agua diagonal central con la marca — difícil de quitar incluso editando con IA',
  'promo.step2.t': 'Publica',
  'promo.step2.d': 'Texto de la publicación generado automáticamente: título, precio de venta/alquiler, dirección, enlace corto — pulsa "Copiar texto" para editarlo',
  'promo.step3.t': 'Mira los números',
  'promo.step3.d': 'Cada publicación incluye seguimiento: clics en FB, vistas de la página de la propiedad — todo visible en el panel',
  'promo.magicBadge': 'La función estrella',
  'promo.magicTitlePre': 'Unas fotos se convierten en',
  'promo.magicTitleHighlight': 'un video inmobiliario profesional.',
  'promo.magicBodyLead': 'Sube las fotos del inmueble y el sistema genera un video 1080p: música automática, subtítulos dinámicos (título/precio/dirección), logo de marca, ',
  'promo.magicBodyBold': 'cero experiencia de edición, cero personal',
  'promo.magicBodyTail': '. Las publicaciones en video alcanzan mucho más que las de fotos en FB — una propiedad, dos formas de jugarla.',
  'promo.bullet1': 'Carrusel de fotos + movimiento Ken Burns',
  'promo.bullet2': 'Cuatro estilos de música, cambio con un toque',
  'promo.bullet3': 'Cuadrado 1:1 / vertical 9:16, válido para FB e IG',
  'promo.compareTitle': 'Hagamos cuentas',
  'promo.compare.col1': 'Contratar / manual',
  'promo.compare.col2': 'Este sistema',
  'promo.compare.r1.c0': 'Una publicación',
  'promo.compare.r1.c1': '30–60 minutos',
  'promo.compare.r1.c2': '10 segundos',
  'promo.compare.r2.c0': 'Un video inmobiliario',
  'promo.compare.r2.c1': 'Medio día + tarifas de edición',
  'promo.compare.r2.c2': '1–2 minutos, generado automáticamente',
  'promo.compare.r3.c0': 'Protección de fotos',
  'promo.compare.r3.c1': 'Ninguna',
  'promo.compare.r3.c2': 'Marca de agua nivel IA integrada',
  'promo.compare.r4.c0': 'Analítica por propiedad',
  'promo.compare.r4.c1': 'Ninguna / herramientas aparte',
  'promo.compare.r4.c2': 'Integrada, lista en el panel',
  'promo.compare.r5.c0': 'Costo mensual',
  'promo.compare.r5.c1': '$8,000–15,000',
  'promo.compare.r5.c2': 'Una tarifa mensual asequible',
  'promo.compareNote': '* El sistema está funcionando hoy en vivo con agentes inmobiliarios',
  'promo.ctaTitlePre': 'La próxima propiedad que entra,',
  'promo.ctaTitleHighlight': 'está en FB 10 segundos después.',
  'promo.ctaSub': 'Reserva una demo en línea de 15 minutos y pruébalo con tu propia propiedad.',
  'promo.ctaNote': 'En la nube · tus datos son tuyos · sin permanencia · soporte dedicado',
  'promo.footer': 'JKD Coding · Using No Way as Way',
  // ===== Distill Destilería del Conocimiento (/distill/) =====
  'distill.meta.title': 'La Destilería del Conocimiento — Convertir la experiencia organizativa en reglas que funcionan｜JKD Coding',
  'distill.meta.description': 'La Destilería del Conocimiento de JKD Coding ayuda a grandes empresas y pymes a extraer conocimiento: convertimos la experiencia dispersa en reuniones, correos y en la mente de los colegas veteranos en reglas ejecutables y reutilizables que los empleados de IA pueden aplicar. Cuando termina la reunión, las reglas ya están en la pared.',
  'distill.hero.kicker': 'Be Water, My Friend',
  'distill.hero.title': 'El conocimiento es agua: toma forma cuando se contiene',
  'distill.hero.tagline': 'LA DESTILERÍA DEL CONOCIMIENTO',
  'distill.hero.lead': 'JKD Coding ofrece extracción de conocimiento para grandes empresas y pymes: destilamos la experiencia dispersa en reuniones, correos y en la mente de los colegas veteranos, y la convertimos en reglas ejecutables y reutilizables que tus empleados de IA pueden aplicar — cuando termina la reunión, las reglas ya están en la pared.',
  'distill.hero.cta1': 'Reserva un diagnóstico de 30 minutos',
  'distill.hero.cta2': 'El método de cuatro pasos',
  'distill.pain.quote.en': 'Absorb what is useful, discard what is useless.',
  'distill.pain.quote.zh': 'Absorbe lo útil, descarta lo inútil — Bruce Lee definió la extracción de conocimiento hace décadas.',
  'distill.pain.heading': 'Las cuatro formas en que se evapora el conocimiento',
  'distill.pain.c1.tt': 'El veterano se jubila',
  'distill.pain.c1.ds': 'Veinte años de criterio se esfuman en una fiesta de despedida — la experiencia nunca salió de su cabeza.',
  'distill.pain.c2.tt': 'El cementerio de actas',
  'distill.pain.c2.ds': 'Guardadas, pero nunca releídas. El archivo número cuatro mil duerme en lo profundo de una carpeta compartida.',
  'distill.pain.c3.tt': 'SOP fosilizados',
  'distill.pain.c3.ds': 'Obsoletos al escribirse — los documentos llegan tarde a la realidad y los nuevos aprenden la manera equivocada.',
  'distill.pain.c4.tt': 'La IA hueca',
  'distill.pain.c4.ds': 'Sin vuestro conocimiento, una IA es solo un chatbot educado.',
  'distill.method.quote.en': "It's not the daily increase but daily decrease — hack away at the unessential.",
  'distill.method.quote.zh': 'No es el aumento diario sino la reducción diaria — la destilación es el arte de cortar. El muro de reglas se talla, no se acumula.',
  'distill.method.heading': 'El ciclo de destilación en cuatro pasos',
  'distill.method.s1.tt': 'Interceptar',
  'distill.method.s1.en': '01 / INTERCEPT',
  'distill.method.s1.ds': 'El día que termina la reunión, su acta entra directa al alambique — antes de que se evapore.',
  'distill.method.s1.out': 'Resultado: conocimiento crudo capturado',
  'distill.method.s2.tt': 'Destilar',
  'distill.method.s2.en': '02 / DISTIL',
  'distill.method.s2.ds': 'IA y consultor cortan juntos: la IA refina, el consultor recorta lo no esencial.',
  'distill.method.s2.out': 'Resultado: reglas candidatas',
  'distill.method.s3.tt': 'Cristalizar',
  'distill.method.s3.en': '03 / CRYSTALLISE',
  'distill.method.s3.ds': 'La revisión humana pone las reglas en la pared — cada una: contexto + acción + responsable.',
  'distill.method.s3.out': 'Resultado: el muro de reglas',
  'distill.method.s4.tt': 'Reutilizar',
  'distill.method.s4.en': '04 / REFLOW',
  'distill.method.s4.ds': 'Los empleados de IA trabajan con las reglas; las revisiones mensuales las afilan con el uso.',
  'distill.method.s4.out': 'Resultado: memoria organizativa',
  'distill.show.kicker': 'THE DISTILLERY · FUNCIÓN PRINCIPAL',
  'distill.show.heading': 'El viaje de una sola gota',
  'distill.show.lead': 'Arriba, el ruido crudo que cae cada día; la única línea dorada del medio es la mano que lo intercepta; abajo, las reglas que se quedan y trabajan.',
  'distill.show.ch1': 'acta-reunion_v7_final(2).docx',
  'distill.show.ch2': 'La 38.ª reunión interdepartamental',
  'distill.show.ch3': '"Dijo que lo hablaríamos después…"',
  'distill.show.ch4': 'Disco compartido (4.200 archivos)',
  'distill.show.ch5': 'El veterano se jubila el mes que viene',
  'distill.show.ch6': '217 correos de ida y vuelta',
  'distill.show.funnel': 'INTERCEPTAR · DESTILAR · DEPOSITAR',
  'distill.show.rule1.tag': 'REGLA · REUNIONES',
  'distill.show.rule1.text': 'Toda reunión termina con "quién + hace qué + para cuándo" — si falta uno, se convoca de nuevo.',
  'distill.show.rule2.tag': 'REGLA · EXTERNO',
  'distill.show.rule2.text': 'Todo compromiso se confirma internamente antes de salir — nadie habla por la empresa a la ligera.',
  'distill.show.rule3.tag': 'REGLA · DOCUMENTOS',
  'distill.show.rule3.text': 'Los procedimientos se revisan cada 90 días — quien escribe firma, quien modifica deja rastro.',
  'distill.show.rule4.tag': 'REGLA · TRASPASO',
  'distill.show.rule4.text': 'Un nuevo empleado lee el muro de reglas en tres días y empieza a trabajar.',
  'distill.show.note': 'Las reglas de ejemplo son ilustraciones genéricas, no la biblioteca real de ningún cliente',
  'distill.proof.kicker': 'PRUEBA DE PRÁCTICA',
  'distill.proof.heading': 'No enseñamos a destilar — destilamos todos los días',
  'distill.proof.s1.n': '252',
  'distill.proof.s1.l': 'reglas de trabajo destiladas de forma continua en nuestro propio sistema de gobernanza',
  'distill.proof.s2.n': 'Semanal',
  'distill.proof.s2.l': 'ritmo de cosecha y revisión, nunca saltado',
  'distill.proof.s3.n': '6 idiomas',
  'distill.proof.s3.l': 'experiencia de gobernanza del conocimiento en todo el sitio, directamente transferible',
  'distill.proof.quote.en': 'Using no way as way — having no limitation as limitation.',
  'distill.proof.quote.zh': 'Sin método como método — las reglas se destilan de la práctica real, no se copian de plantillas.',
  'distill.fit.heading': 'Para quién es este servicio',
  'distill.fit.f1.tt': 'ONG',
  'distill.fit.f1.ds': 'Las relaciones con donantes y la experiencia de voluntarios dejan de irse con cada rotación.',
  'distill.fit.f2.tt': 'Empresas sociales',
  'distill.fit.f2.ds': 'La misión y la experiencia operativa se heredan sin deformarse al crecer.',
  'distill.fit.f3.tt': 'Pymes',
  'distill.fit.f3.ds': 'Convertir el criterio del dueño en una institución que toda la empresa puede usar.',
  'distill.fit.f4.tt': 'Grandes empresas',
  'distill.fit.f4.ds': 'Gobernanza del conocimiento entre departamentos: la experiencia tácita de cada equipo se vuelve reglas ejecutables para empleados de IA.',
  'distill.faq.q1': '¿Cuánto tiempo lleva la destilación?',
  'distill.faq.a1': 'La primera ronda apunta a la entrega el mismo día: cuando termina la reunión, sus reglas están en la pared. El ciclo completo — interceptar, destilar, cristalizar, reutilizar — avanza semanalmente según el tamaño de la organización y sigue ciclando con revisiones mensuales.',
  'distill.faq.q2': '¿Se filtrará nuestro conocimiento?',
  'distill.faq.a2': 'No. Las reglas destiladas son tuyas y viven dentro de tu jurisdicción; las tratamos solo durante el encargo con tu autorización, y nos retiramos tras la entrega.',
  'distill.faq.q3': '¿En qué se diferencia de las herramientas de gestión de conocimiento con IA?',
  'distill.faq.a3': 'Las herramientas almacenan; la destilación corta y da forma. Sin la estructura de regla "quién + hace qué + para cuándo", más datos siguen siendo inejecutables — entregamos criterio, no una base de datos.',
  'distill.faq.q4': '¿Cómo se conectan las reglas destiladas con los empleados de IA?',
  'distill.faq.a4': 'El muro de reglas es la base de trabajo del empleado de IA: el gestor de proyectos de IA actúa según las reglas y confirma internamente con ellas, y las revisiones mensuales devuelven nueva experiencia como nuevas reglas — un circuito cerrado.',
  'distill.faq.q5': '¿El enfoque difiere entre grandes empresas y pymes?',
  'distill.faq.a5': 'Las pymes se centran en institucionalizar el criterio del dueño; las grandes empresas necesitan gobernanza entre departamentos — la experiencia tácita de cada equipo unificada en conjuntos de reglas sin conflictos y ejecutables.',
  'distill.faq.q6': '¿Cómo empezamos?',
  'distill.faq.a6': 'Empezando por un diagnóstico de 30 minutos: inventaría los activos de conocimiento más valiosos — y más en riesgo (evaporándose) — de tu organización y decide el alcance y el ritmo de la destilación.',
  'distill.funnel.heading': 'De una gota de ruido a un equipo de empleados de IA',
  'distill.funnel.f1.tag': 'PASO 1',
  'distill.funnel.f1.tt': 'Diagnóstico',
  'distill.funnel.f1.ds': 'Una sesión uno a uno de 30 minutos para inventariar los activos de conocimiento más valiosos — y más en riesgo (evaporándose).',
  'distill.funnel.f2.tag': 'PASO 2 · ESTA PÁGINA',
  'distill.funnel.f2.tt': 'Destilación del conocimiento',
  'distill.funnel.f2.ds': 'El ciclo de cuatro pasos pone las reglas en la pared — el conocimiento por fin tiene forma.',
  'distill.funnel.f3.tag': 'PASO 3',
  'distill.funnel.f3.tt': 'Empleados de IA gestionados',
  'distill.funnel.f3.ds': 'Gestores de proyecto de IA en el sitio, trabajando desde tu muro de reglas — vendemos empleados, no software.',
  'distill.cta1': 'Hablar por WhatsApp',
  'distill.cta2': 'Sobre el diagnóstico',
  'distill.wa.text': '¡Hola! Me gustaría conocer el servicio de la Destilería del Conocimiento de JKD Coding.',
  'distill.footer': 'JKD Coding · Using no way as way',
  'distill.card.title': 'Extracción de conocimiento',
  'distill.card.desc': 'Destila la experiencia dispersa en reuniones y en colegas veteranos y conviértela en reglas ejecutables que tus empleados de IA pueden aplicar.',
  'distill.card.cta': 'Entrar a la Destilería',
  'distill.xs.title': 'Después del diagnóstico: destila los hallazgos en reglas',
  'distill.xs.body': 'El diagnóstico encuentra el cuello de botella; la Destilería del Conocimiento convierte la experiencia organizativa en reglas ejecutables, reutilizables y aplicables por empleados de IA — cuando termina la reunión, las reglas ya están en la pared.',
  'distill.xs.cta': 'Entrar a la Destilería',

  // ============ Términos y Condiciones /terms (3 pestañas: Privacidad / Reembolso / Entrega) ============
  'terms.meta.title': 'Términos y Condiciones — Política de Privacidad, Reembolso y Entrega | JKD Coding',
  'terms.meta.description': 'Términos y condiciones de JKD Coding (operado por Wiseman Consultancy Limited): Política de Privacidad, Política de Reembolso y Política de Entrega, aplicables a este sitio web y a todos los servicios de consultoría y desarrollo digital.',
  'terms.title': 'Términos y Condiciones',
  'terms.subtitle': 'Estos términos aplican al sitio web de JKD Coding (jkdcoding.com) y a todos los servicios de consultoría y digitales que ofrecemos. El sitio y los servicios son operados por Wiseman Consultancy Limited.',
  'terms.updated': 'Última actualización: 2026-10-13',
  'terms.tab.privacy': 'Política de Privacidad',
  'terms.tab.refund': 'Política de Reembolso',
  'terms.tab.delivery': 'Política de Entrega',
  'terms.contact': 'Si tienes preguntas, escríbenos por WhatsApp al +852 5165 1480.',

  // — Política de Privacidad —
  'terms.privacy.note': 'Esta política explica cómo JKD Coding (operado por **Wiseman Consultancy Limited**) recopila, usa y protege tus datos. Al usar este sitio o nuestros servicios, aceptas estos términos.',
  'terms.privacy.h1': '1. Datos que recopilamos',
  'terms.privacy.s1': '- **Datos de consulta y contacto**: nombre, número de WhatsApp, correo electrónico, organización (mediante reserva de diagnóstico, WhatsApp o formularios)\n- **Datos del proyecto**: material de negocio, contenido de la base de conocimiento y recursos web que nos proporciones\n- **Datos de navegación**: vistas de página, referentes y parámetros de marketing (estadísticas anónimas para mejorar el sitio y el rendimiento publicitario)\n- **Datos de pago**: solo conservamos números de factura e importes, y **nunca vemos ni almacenamos los datos de tu medio de pago**',
  'terms.privacy.h2': '2. Cómo usamos los datos',
  'terms.privacy.s2': '- Prestar, operar y mejorar nuestros servicios de consultoría y desarrollo\n- Presupuestos, gestión de proyectos, soporte técnico y facturación\n- Avisos esenciales del servicio (confirmaciones de reserva, arreglos de entrega, alertas de seguridad)',
  'terms.privacy.h3': '3. Procesamiento por terceros (motor de IA)',
  'terms.privacy.s3': 'Para prestar servicios de IA, el contenido que nos proporciones puede transmitirse a los proveedores de grandes modelos de lenguaje que utilizamos (incluidos DeepSeek, Z.ai y otros), que procesan los datos conforme a sus propias políticas de privacidad. Solo transmitimos el contenido necesario para completar el trabajo y revisamos continuamente sus medidas de protección de datos.',
  'terms.privacy.h4': '4. Conservación de datos',
  'terms.privacy.s4': '- Registros de consultas y conversaciones: conservados unos 24 meses y luego purgados\n- Archivos del proyecto: conservados 90 días tras la entrega para correcciones y soporte; después se eliminan a petición o se purgan automáticamente',
  'terms.privacy.h5': '5. Lo que nunca hacemos',
  'terms.privacy.s5': '- Nunca vendemos ni alquilamos tus datos\n- Nunca usamos tus datos — ni los de tus clientes — para marketing de terceros\n- Nunca divulgamos datos a terceros salvo obligación legal o necesidad para prestar el servicio (p. ej. motores de IA, proveedores de alojamiento y de pagos)',
  'terms.privacy.h6': '6. Tus derechos',
  'terms.privacy.s6': 'Puedes acceder, corregir o eliminar tus datos personales. Envía tu solicitud por WhatsApp al +852 5165 1480; respondemos en un plazo de **14 días**. Tratamos los datos personales conforme a la Ordenanza de Datos Personales (Privacidad) (Cap. 486, Leyes de Hong Kong).',
  'terms.privacy.h7': '7. Actualizaciones de la política',
  'terms.privacy.s7': 'Los cambios sustanciales se publicarán en esta página con la fecha actualizada. Los cambios que afecten sustancialmente al uso del servicio se notificarán por separado.',

  // — Política de Reembolso —
  'terms.refund.note': 'JKD Coding ofrece servicios digitales de consultoría y desarrollo (sin productos físicos). Todos los precios están en HKD. Te recomendamos reservar primero el **diagnóstico gratuito** (30 minutos, uno a uno) y decidir con claridad antes de pagar.',
  'terms.refund.h1': '1. Diagnóstico gratuito',
  'terms.refund.s1': 'El diagnóstico es totalmente gratuito y sin presión de venta. Todo el trabajo de pago se presupuesta tras el diagnóstico y comienza solo con tu confirmación por escrito.',
  'terms.refund.h2': '2. Cancelación antes de iniciar el trabajo',
  'terms.refund.s2': 'Si cancelas tras la confirmación pero antes de que empiece el trabajo: **se reembolsa la totalidad** de los pagos. Los costes de terceros ya incurridos en tu nombre (dominio, licencias, recursos en la nube, etc.) se deducen al coste con justificantes.',
  'terms.refund.h3': '3. Planes listos y biblioteca de componentes',
  'terms.refund.s3': 'Dentro de los **7 días** posteriores al pago, si el servicio no puede entregarse u operar por causa nuestra y no podemos repararlo en un plazo razonable, puedes solicitar un **reembolso completo**. Una vez entregado con éxito y funcionando con normalidad, el servicio se considera completado y no es reembolsable.',
  'terms.refund.h4': '4. Proyectos a medida (por hitos)',
  'terms.refund.s4': '- Los proyectos a medida se facturan por hitos: los **hitos iniciados** se liquidan según el avance real; los **hitos no iniciados** son reembolsables\n- Cancelación a mitad de camino: el trabajo completado y aceptado no es reembolsable; el resto se reembolsa de forma proporcional\n- Las solicitudes de cambio tras la entrega son alcance nuevo y se presupuestan aparte',
  'terms.refund.h5': '5. Errores de facturación',
  'terms.refund.s5': 'Los cargos duplicados o incorrectos, una vez verificados, se reembolsan íntegramente al medio de pago original dentro de **7 días hábiles**.',
  'terms.refund.h6': '6. Cómo solicitarlo',
  'terms.refund.s6': 'WhatsApp +852 5165 1480 (indica tu número de factura). Todas las solicitudes se procesan en un plazo de **7 días hábiles**; el reembolso se devuelve por el canal de pago original.',

  // — Política de Entrega —
  'terms.delivery.note': 'JKD Coding ofrece servicios puramente digitales — **no hay envío físico**. Toda la entrega es electrónica; no se aplican gastos de envío.',
  'terms.delivery.h1': '1. Plazos de entrega',
  'terms.delivery.s1': '- **Planes listos**: despliegue en tan solo 30 minutos tras confirmar requisitos y recibir el pago (horario laboral)\n- **Biblioteca de componentes**: licencias y documentación enviadas por correo/WhatsApp dentro del día hábil siguiente al pago\n- **Proyectos a medida**: entregados por hitos contractuales, normalmente en línea en 6–8 semanas\n- Las solicitudes fuera de horario se atienden en la siguiente sesión laboral; todas las consultas se responden en 24 horas',
  'terms.delivery.h2': '2. Qué se entrega',
  'terms.delivery.s2': 'Según el plan, la entrega suele incluir: acceso al sitio/sistema, código fuente o entorno de despliegue, guías de configuración y uso, y el canal de soporte técnico.',
  'terms.delivery.h3': '3. Método de entrega',
  'terms.delivery.s3': 'Todo se entrega electrónicamente (web, correo, WhatsApp). Una vez completado el despliegue y aceptada la obra por ambas partes, el servicio se considera entregado e iniciado.',
  'terms.delivery.h4': '4. Retrasos en la entrega',
  'terms.delivery.s4': 'Si la entrega supera el plazo prometido, puedes elegir la **reentrega inmediata** o solicitar el reembolso conforme a la [Política de Reembolso](#refund).',

  // — Enlace de términos en el pie —
  'footer.termsLink': 'Términos y Condiciones',
};

export default dict;
